import {
  ActiveFilters,
  EMPTY_ACTIVE_FILTERS,
  EMPTY_FACETS,
  FACET_KEYS,
  FacetKey,
  ProductsResponse,
  SearchResponse,
} from "@/@types/filters";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:5000";

export const PAGE_SIZE = 28;

export interface ProductsQuery {
  category?: string;
  filters?: ActiveFilters;
  /** Quantas páginas já foram carregadas (o "Ver mais" acumula no servidor). */
  page?: number;
  novidades?: boolean;
  exclusivos?: boolean;
  kosher?: boolean;
  transforma?: boolean;
  sazonal?: boolean;
  search?: string;
  /** Janela explícita (o "Ver mais" pede só a próxima página). Sem ela, vale a
      lista acumulada até `page`, que é o que o servidor renderiza num link
      aberto direto em `?pagina=N`. */
  offset?: number;
  limit?: number;
}

interface RequestOptions {
  secret?: string;
  auth?: string;
}

/**
 * Monta a querystring sempre com URLSearchParams. Concatenar à mão era a causa
 * de valores como "ACRÍLICO & PLÁSTICO" truncarem a URL no `&`.
 */
function pageWindow(query: ProductsQuery) {
  if (query.limit) return { offset: Math.max(query.offset ?? 0, 0), limit: query.limit };

  return { offset: 0, limit: Math.max(query.page ?? 1, 1) * PAGE_SIZE };
}

export function buildProductsUrl(query: ProductsQuery): string {
  const params = new URLSearchParams();
  const { offset, limit } = pageWindow(query);

  params.set("offset", String(offset));
  params.set("limit", String(limit));

  if (query.category) params.set("category", query.category);
  if (query.search) params.set("search", query.search);

  FACET_KEYS.forEach((key) => {
    const values = query.filters?.[key];
    if (values?.length) params.set(key, values.join(","));
  });

  // Só enviamos as flags quando são verdadeiras: antes iam como a string
  // "undefined" e a API tratava qualquer valor não-"true" como falso por sorte.
  (["novidades", "exclusivos", "kosher", "transforma", "sazonal"] as const).forEach((flag) => {
    if (query[flag]) params.set(flag, "true");
  });

  return `${API_URL}/api/products?${params.toString()}`;
}

/**
 * Busca textual. Usa /api/search (trigram + tsvector + embeddings), que é a
 * engine boa da API — o front antes chamava /api/products?search=, um caminho
 * legado sem paginação nem facetas.
 */
export function buildSearchUrl(query: ProductsQuery & { search: string }): string {
  const params = new URLSearchParams();
  const { offset, limit } = pageWindow(query);

  params.set("search", query.search);
  params.set("limit", String(limit));
  params.set("offset", String(offset));

  if (query.category) params.set("category", query.category);
  if (query.kosher) params.set("kosher", "true");

  FACET_KEYS.forEach((key) => {
    const values = query.filters?.[key];
    if (values?.length) params.set(key, values.join(","));
  });

  return `${API_URL}/api/search?${params.toString()}`;
}

export async function searchProducts(
  query: ProductsQuery & { search: string },
  options: RequestOptions = {}
): Promise<SearchResponse> {
  const response = await fetch(buildSearchUrl(query), {
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      ...(options.secret && { "X-Api-Key": options.secret }),
      ...(options.auth && { Authorization: `Bearer ${options.auth}` }),
    },
  });

  if (!response.ok) {
    throw new Error(`Falha na busca: ${response.status}`);
  }

  return normalizeResponse(await response.json());
}

export async function fetchProducts(
  query: ProductsQuery,
  options: RequestOptions = {}
): Promise<ProductsResponse> {
  const response = await fetch(buildProductsUrl(query), {
    // Listagens variam por X-Api-Key (showroom vê preço, anônimo não), então
    // não podem ser cacheadas de forma compartilhada.
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      ...(options.secret && { "X-Api-Key": options.secret }),
      ...(options.auth && { Authorization: `Bearer ${options.auth}` }),
    },
  });

  if (!response.ok) {
    throw new Error(`Falha ao buscar produtos: ${response.status}`);
  }

  return normalizeResponse(await response.json());
}

/** Protege o front de respostas antigas da API, sem `facets`. */
export function normalizeResponse(json: any): ProductsResponse {
  return {
    ...json,
    facets: { ...EMPTY_FACETS, ...(json?.facets ?? {}) },
    applied_filters: { ...EMPTY_ACTIVE_FILTERS, ...(json?.applied_filters ?? {}) },
  };
}

/**
 * Lê os filtros da URL. Aceita o formato novo (?cor=VERDE,AZUL) e o antigo
 * (?filters=cor:VERDE|material:VIDRO), para não quebrar links compartilhados.
 */
export function parseFiltersFromParams(
  params: URLSearchParams | Record<string, string | string[] | undefined>
): ActiveFilters {
  const get = (key: string): string | undefined => {
    if (params instanceof URLSearchParams) return params.get(key) ?? undefined;
    const value = params[key];
    return Array.isArray(value) ? value[0] : value;
  };

  const filters: ActiveFilters = {
    kosher_tipo: [],
    cor: [],
    material: [],
    especificacao: [],
    produto: [],
  };

  const legacy = get("filters");
  if (legacy) {
    legacy.split("|").forEach((token) => {
      const [key, ...rest] = token.split(":");
      const facetKey = key?.toLowerCase() as FacetKey;
      const value = rest.join(":");
      if (FACET_KEYS.includes(facetKey) && value) {
        filters[facetKey].push(value.toUpperCase());
      }
    });
  }

  FACET_KEYS.forEach((key) => {
    const raw = get(key);
    if (!raw) return;
    // O formato novo tem precedência sobre o legado.
    filters[key] = raw
      .split(",")
      .map((value) => value.trim().toUpperCase())
      .filter(Boolean);
  });

  return filters;
}

export function countActiveFilters(filters: ActiveFilters): number {
  return FACET_KEYS.reduce((total, key) => total + filters[key].length, 0);
}

export function hasLegacyFilterParam(
  params: Record<string, string | string[] | undefined>
): boolean {
  return Boolean(params?.filters);
}

/** Converte filtros ativos em querystring, para navegação no cliente. */
export function filtersToSearchParams(
  filters: ActiveFilters,
  base?: URLSearchParams
): URLSearchParams {
  const params = new URLSearchParams(base?.toString() ?? "");

  // O formato legado deixa de existir assim que o usuário mexe num filtro.
  params.delete("filters");

  FACET_KEYS.forEach((key) => {
    const values = filters[key];
    if (values.length) {
      params.set(key, values.join(","));
    } else {
      params.delete(key);
    }
  });

  return params;
}

/**
 * Identifica a lista (termo, flags, facetas) sem a página: o "Ver mais"
 * continua a mesma lista, não começa uma nova.
 */
export function queryKey(params: URLSearchParams): string {
  const copy = new URLSearchParams(params);
  copy.delete("pagina");
  copy.sort();
  return copy.toString();
}

/** Acrescenta a próxima página sem repetir o que já está na tela: a lista pode
    mudar na API entre um pedido e outro e deslocar os produtos. */
export function appendUnique<T extends { id: unknown }>(items: T[], more: T[]): T[] {
  const seen = new Set(items.map((item) => item.id));
  return [...items, ...more.filter((item) => !seen.has(item.id))];
}

/**
 * Grava a página na URL sem navegar: uma navegação faria o Next renderizar de
 * novo, no servidor, a lista acumulada inteira. O estado do router do Next
 * continua na entrada do histórico; sem ele, o "voltar" recarregaria a página.
 */
export function replacePageInUrl(page: number) {
  const url = new URL(window.location.href);

  if (page > 1) {
    url.searchParams.set("pagina", String(page));
  } else {
    url.searchParams.delete("pagina");
  }

  window.history.replaceState(window.history.state, "", url);
}
